import { ensureArray } from "./JSUtils";
import { getPropUpdateDetails, getUpdateType, redirectUpdate } from "./PropUpdateUtils";
import { SYNCED_PROPS } from "@/constants/DesignConstants.mjs";
import AppConstants from "@/constants/AppConstants";
import { deleteProp } from "./PropUpdateUtils";
import { prepForInherit, getProp } from "./Merge/MergeUtils.mjs";

const { UPDATE_ACTION_SET } = AppConstants;

/**
 * @typedef {import("immutable").Map} Map
 * @typedef {import("@/vo/BlockUpdate.mjs").PropUpdate} PropUpdate
 * @typedef {typeof import("@/constants/DesignConstants.mjs").LAYOUTS} LAYOUTS
 */

class UpdateBuilder {
  #elementData;
  #basePropInfos;
  #theme;
  #baseUpdate;
  #updateType;
  #updateLayout;
  #updatePropName;
  #mergeUpdateIndex;
  #newUpdates;

  /**
   * Generates a builder to handle an existing set of propUpdates.
   * @param {{
   *  elementData: Map,
   *  propInfo: PropUpdate | Array<PropUpdate>,
   *  layout: LAYOUTS[number],
   *  theme: Map
   * }} props
   */
  constructor({ elementData, propInfo = [], theme }) {
    this.#elementData = elementData;
    this.#basePropInfos = ensureArray(propInfo);
    // this.#layout = layout;
    this.#theme = theme;

    // base update stuff
    // ? this part might be a mistake
    this.#baseUpdate;
    this.#updateLayout;
    this.#updatePropName;

    // update "type" stuff
    this.#newUpdates = [...this.#basePropInfos];
    this.#mergeUpdateIndex;
    this.#updateType;

    // collects base update info and merge update infos
    if (this.#basePropInfos.length) {
      this.#baseUpdate = this.#basePropInfos[0];
      this.#collectUpdateInfos();
    }

    this.#updateMergeIndex();
  }

  // * internal methods

  /**
   * Collects data describing the update-set. What the base update/
   */
  #collectUpdateInfos() {
    [this.#updatePropName, this.#updateLayout] = getPropUpdateDetails(this.#baseUpdate);
  }

  /**
   * Updates the tracked index of a merge update
   */
  #updateMergeIndex() {
    this.#mergeUpdateIndex = this.#newUpdates.findIndex((update) => update?.name === SYNCED_PROPS);
  }

  /**
   * Updates merge array through an update. Generates a sync update if one doesn't exist.
   * @param {(originalProps: Array<string>) => Array<string>} callback
   */
  #updateMergeProps(callback) {
    const newMergeIndex =
      this.#mergeUpdateIndex === -1 ? this.#newUpdates.length : this.#mergeUpdateIndex;

    const baseMergeProps =
      this.#newUpdates[newMergeIndex]?.value ?? this.#elementData.get(SYNCED_PROPS, []);

    const newSyncUpdate = {
      name: SYNCED_PROPS,
      value: callback(baseMergeProps),
      action: UPDATE_ACTION_SET,
      addUndo: false,
    };

    this.#newUpdates[newMergeIndex] = newSyncUpdate;
    this.#updateMergeIndex();
  }

  // * merge stuff

  /**
   * Adds a prop name to the sync array. Adds an update for the array if one doesn't exist.
   * @param {string} propName
   * @returns
   */

  addMergeProp(propName) {
    this.#updateMergeProps((mergeProps) => Array.from(new Set([...mergeProps, propName])));
    return this;
  }

  /**
   * Removes a prop from the sync array. Adds an update for the array if one doesn't exist.
   * @param {string} propName
   * @returns
   */
  removeMergeProp(propName) {
    this.#updateMergeProps((mergeProps) =>
      Array.from(new Set(mergeProps.filter((prop) => prop !== propName)))
    );
    return this;
  }

  // * other public methods
  /**
   * Adds a propUpdate.
   * @param {Array<PropUpdate>} propInfo
   */
  addUpdate(...propInfo) {
    const updates = ensureArray(propInfo);

    updates.forEach((singlePropUpdate) => {
      const update = { ...singlePropUpdate };
      this.#replaceUpdate(update);
    });

    return this;
  }

  /**
   * Adds or swaps base update.
   * @param {PropUpdate} propInfo
   */
  addBaseUpdate(propInfo) {
    const update = { ...propInfo };

    this.#replaceUpdate(update);
    this.#baseUpdate = update;
    this.#collectUpdateInfos();

    return this;
  }

  /**
   * Replaces an existing prop update with the passes one when names match (and exists). Otherwise adds to the end.
   * @param {PropUpdate} singlePropUpdate
   */
  #replaceUpdate(singlePropUpdate) {
    const existingUpdateIndex = this.findUpdateIndex({ name: singlePropUpdate.name });
    const newIndex = existingUpdateIndex !== -1 ? existingUpdateIndex : this.#newUpdates.length;

    this.#newUpdates[newIndex] = singlePropUpdate;
  }

  /**
   * Adds a deletion update for a prop.
   * @param {string} propName
   * @param {LAYOUTS[number]=} layout
   */
  deleteProp(propName, layout) {
    this.#newUpdates.push(deleteProp(`${layout ? `${layout}.` : ""}${propName}`));
    return this;
  }

  /**
   * Direct base update to another layout (or neither)
   * @param {LAYOUTS[number]=} layout
   * @returns
   */
  redirectBaseUpdate(layout) {
    this.#baseUpdate = redirectUpdate(this.#baseUpdate, layout);
    return this;
  }

  /**
   * Generates and adds a propUpdate to inherit a value. Returns the update if successful.
   * @param {string} propName
   * @param {LAYOUTS[number]=} layout
   * @returns {PropUpdate=}
   */
  generateInheritProp(propName, layout) {
    if (this.#theme) {
      const [inheritElement, inheritPropName] = prepForInherit(
        // @ts-ignore
        this.#elementData,
        layout,
        propName,
        this.#theme
      );

      // ? are there any instances where we'd want an undefined to be inherited (a delete)?
      const value = inheritPropName ? getProp(inheritElement, layout, inheritPropName) : undefined;

      // bail out if a propName isn't found to prevent returning the entire inheritElement
      if (inheritPropName && value !== undefined) {
        const inheritPropUpdate = {
          ...(this.#baseUpdate ?? {}),
          name: `${layout}.${propName}`,
          value: getProp(inheritElement, layout, inheritPropName),
          addUndo: false,
        };

        this.#newUpdates.push(inheritPropUpdate);

        return inheritPropUpdate;
      }
    }
  }

  /**
   * Wraps generateInheritProp to continue building.
   * @param {string} propName
   * @param {LAYOUTS[number]=} layout
   * @returns {UpdateBuilder}
   */
  inheritProp(propName, layout) {
    this.generateInheritProp(propName, layout);

    return this;
  }

  /**
   * Searches for an update using direct matches for key/value
   * @param {Partial<PropUpdate>} searchProps
   * @param {boolean} fuzzy - looser search for prop in search props
   * @returns {PropUpdate=}
   */
  findUpdate(searchProps = {}, fuzzy = false) {
    const index = this.findUpdateIndex(searchProps, fuzzy);
    const update = this.#newUpdates[index];
    return update ? { ...update } : undefined;
  }

  /**
   * Searches for an update using direct matches for key/value
   * @param {Partial<PropUpdate>} searchProps
   * @param {boolean} fuzzy - looser search for prop in search props
   * @returns {number}
   */
  findUpdateIndex(searchProps = {}, fuzzy = false) {
    return this.#newUpdates.findIndex((update) =>
      Object.entries(searchProps).every(([searchKey, searchValue]) =>
        searchKey in update
          ? fuzzy && typeof searchValue === "string" && typeof update[searchKey] === "string"
            ? update[searchKey].includes(searchValue)
            : update[searchKey] === searchValue
          : false
      )
    );
  }

  removeUpdate(searchProps = {}) {
    const updateIndex = this.findUpdateIndex(searchProps);
    if (updateIndex !== -1) {
      const newUpdates = [...this.#newUpdates];
      newUpdates.splice(updateIndex, 1);
      this.#newUpdates = newUpdates;

      // go back to this when I figure out jest node versioning. Way more concise.
      // this.#newUpdates = this.#newUpdates.toSpliced(updateIndex, 1);
    }

    return this;
  }

  /**
   * Adds undo to base so an undo/redo point will be added to the stack when collapsed.
   * @returns {UpdateBuilder}
   */
  addUndo() {
    const update = this.#newUpdates[0];
    update && (update.addUndo = true);

    return this;
  }

  getPropName() {
    return this.#updatePropName;
  }

  /**
   * Effectively the same as getPropUpdateDetails but for the base update.
   * @returns {[string, LAYOUTS[number]]}
   */
  getUpdateInfo() {
    return [this.#updatePropName, this.#updateLayout];
  }

  collectChanges() {
    return [...this.#newUpdates];
  }
}

export default UpdateBuilder;
